import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Skeleton,
  Text,
  Flex,
  Badge,
  Stack,
  SimpleGrid,
  Divider,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Textarea,
} from "@chakra-ui/react";
import {
  BsEye,
  BsSearch,
  BsCheckCircle,
  BsXCircle,
  BsArrowCounterclockwise,
  BsThreeDotsVertical,
} from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import procurementApi from "../services/procurementApi";
import StatusBadge from "../components/StatusBadge";

const ApprovalList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve', 'reject', 'sendback'
  const [remarks, setRemarks] = useState("");

  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [requests, searchTerm, priorityFilter]);

  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      const response = await procurementApi.getPendingApprovals();
      const requestsData = response?.data || [];
      console.log("📋 Fetched pending approvals:", requestsData);
      setRequests(requestsData);
    } catch (error) {
      console.error("❌ Error fetching pending approvals:", error);
      toast({
        title: "Error fetching approvals",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];

    if (searchTerm) {
      filtered = filtered.filter(
        (req) =>
          req.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (priorityFilter) {
      filtered = filtered.filter((req) => req.priority === priorityFilter);
    }

    setFilteredRequests(filtered);
  };

  const handleActionClick = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setRemarks("");
    onOpen();
  };

  const handleActionSubmit = async () => {
    if (!selectedRequest || !actionType) return;

    try {
      setActionLoading(true);

      const normalizedAction =
        actionType === "sendback" ? "SEND_BACK" : actionType.toUpperCase();

      const payload = {
        action: normalizedAction,
        remarks: remarks,
      };

      // Call the action endpoint
      await procurementApi.performAction(selectedRequest._id, payload);

      toast({
        title: `Request ${actionType}d successfully`,
        status: "success",
        duration: 2000,
      });

      // Refresh the list
      fetchPendingApprovals();
      onClose();
    } catch (error) {
      console.error(`Error ${actionType}ing request:`, error);
      toast({
        title: `Error ${actionType}ing request`,
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleView = (id) => {
    navigate(`/procurement/requests/${id}`);
  };

  const getActionButtonColor = (action) => {
    switch (action) {
      case "approve":
        return "green";
      case "reject":
        return "red";
      case "sendback":
        return "orange";
      default:
        return "gray";
    }
  };

  const getActionButtonIcon = (action) => {
    switch (action) {
      case "approve":
        return BsCheckCircle;
      case "reject":
        return BsXCircle;
      case "sendback":
        return BsArrowCounterclockwise;
      default:
        return BsCheckCircle;
    }
  };

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="600px" />
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      {/* Header */}
      <Flex
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 0 }}
        mb={6}
      >
        <Heading size="lg">Pending Approvals</Heading>
        <HStack>
          <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
            {filteredRequests.length} Pending
          </Badge>
        </HStack>
      </Flex>

      {/* Filters */}
      <Stack direction={{ base: "column", md: "row" }} spacing={3} mb={6}>
        <InputGroup maxW={{ base: "full", md: "400px" }}>
          <InputLeftElement pointerEvents="none">
            <BsSearch />
          </InputLeftElement>
          <Input
            placeholder="Search by title, ID, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        <Select
          placeholder="All Priorities"
          maxW={{ base: "full", md: "200px" }}
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
        >
          <option value="LOW">Low</option>
          <option value="MEDIUM">Medium</option>
          <option value="HIGH">High</option>
          <option value="URGENT">Urgent</option>
        </Select>

        <Button
          variant="ghost"
          w={{ base: "full", md: "auto" }}
          onClick={() => {
            setSearchTerm("");
            setPriorityFilter("");
          }}
        >
          Clear Filters
        </Button>
      </Stack>

      {/* Mobile cards */}
      <Box display={{ base: "block", md: "none" }}>
        {filteredRequests.length === 0 ? (
          <Box
            bg={bgColor}
            border="1px"
            borderColor={borderColor}
            borderRadius="md"
            p={8}
            textAlign="center"
          >
            <Text fontSize="lg" color="gray.500">
              {requests.length === 0
                ? "No pending approvals at this time"
                : "No requests match your filters"}
            </Text>
          </Box>
        ) : (
          <SimpleGrid columns={1} spacing={3}>
            {filteredRequests.map((request) => (
              <Box
                key={request._id}
                bg={bgColor}
                border="1px"
                borderColor={borderColor}
                borderRadius="lg"
                p={4}
              >
                <HStack justify="space-between" align="start" mb={2}>
                  <Box maxW="70%">
                    <Text fontSize="xs" color="gray.500">
                      {request.requestId || request._id?.slice(-8) || "N/A"}
                    </Text>
                    <Text fontWeight="semibold" noOfLines={2}>
                      {request.title}
                    </Text>
                  </Box>
                  <StatusBadge status={request.status} />
                </HStack>

                <Divider my={2} />

                <SimpleGrid columns={2} spacing={2} mb={3}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Priority
                    </Text>
                    {request.priority ? (
                      <Badge
                        mt={1}
                        colorScheme={
                          request.priority === "URGENT"
                            ? "red"
                            : request.priority === "HIGH"
                              ? "orange"
                              : request.priority === "MEDIUM"
                                ? "blue"
                                : "gray"
                        }
                      >
                        {request.priority}
                      </Badge>
                    ) : (
                      <Text fontSize="sm">-</Text>
                    )}
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Stage
                    </Text>
                    <Text fontSize="sm" noOfLines={1}>
                      {request.workflowId?.stages?.[request.workflowStage]
                        ?.stageName || "N/A"}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Requested By
                    </Text>
                    <Text fontSize="sm">
                      {request.createdBy?.name || "N/A"}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Created Date
                    </Text>
                    <Text fontSize="sm">
                      {new Date(request.createdAt).toLocaleDateString()}
                    </Text>
                  </Box>
                </SimpleGrid>

                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Button
                    size="sm"
                    leftIcon={<BsEye />}
                    variant="outline"
                    onClick={() => handleView(request._id)}
                  >
                    View
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<BsCheckCircle />}
                    colorScheme="green"
                    variant="outline"
                    onClick={() => handleActionClick(request, "approve")}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<BsXCircle />}
                    colorScheme="red"
                    variant="outline"
                    onClick={() => handleActionClick(request, "reject")}
                  >
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<BsArrowCounterclockwise />}
                    colorScheme="orange"
                    variant="outline"
                    onClick={() => handleActionClick(request, "sendback")}
                  >
                    Send Back
                  </Button>
                </Stack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>

      {/* Table */}
      <Box display={{ base: "none", md: "block" }}>
        {filteredRequests.length === 0 ? (
          <Box
            bg={bgColor}
            border="1px"
            borderColor={borderColor}
            borderRadius="md"
            p={8}
            textAlign="center"
          >
            <Text fontSize="lg" color="gray.500">
              {requests.length === 0
                ? "No pending approvals at this time"
                : "No requests match your filters"}
            </Text>
          </Box>
        ) : (
          <Box
            bg={bgColor}
            border="1px"
            borderColor={borderColor}
            borderRadius="md"
            overflowX="auto"
          >
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Request ID</Th>
                  <Th>Title</Th>
                  <Th>Priority</Th>
                  <Th>Stage</Th>
                  <Th>Status</Th>
                  <Th>Requested By</Th>
                  <Th>Created Date</Th>
                  <Th textAlign="center">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredRequests.map((request) => (
                  <Tr key={request._id} _hover={{ bg: "gray.50" }}>
                    <Td fontWeight="medium">
                      {request.requestId || request._id?.slice(-8) || "N/A"}
                    </Td>
                    <Td maxW="300px" isTruncated>
                      {request.title}
                    </Td>
                    <Td>
                      {request.priority ? (
                        <Badge
                          colorScheme={
                            request.priority === "URGENT"
                              ? "red"
                              : request.priority === "HIGH"
                                ? "orange"
                                : request.priority === "MEDIUM"
                                  ? "blue"
                                  : "gray"
                          }
                        >
                          {request.priority}
                        </Badge>
                      ) : (
                        "-"
                      )}
                    </Td>
                    <Td>
                      {request.workflowId?.stages?.[request.workflowStage]
                        ?.stageName || "N/A"}
                    </Td>
                    <Td>
                      <StatusBadge status={request.status} />
                    </Td>
                    <Td>{request.createdBy?.name || "N/A"}</Td>
                    <Td>{new Date(request.createdAt).toLocaleDateString()}</Td>
                    <Td>
                      <HStack spacing={2} justify="center">
                        <IconButton
                          icon={<BsEye />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          aria-label="View details"
                          onClick={() => handleView(request._id)}
                        />
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<BsThreeDotsVertical />}
                            size="sm"
                            variant="ghost"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<BsCheckCircle />}
                              onClick={() =>
                                handleActionClick(request, "approve")
                              }
                            >
                              Approve
                            </MenuItem>
                            <MenuItem
                              icon={<BsXCircle />}
                              onClick={() =>
                                handleActionClick(request, "reject")
                              }
                            >
                              Reject
                            </MenuItem>
                            <MenuItem
                              icon={<BsArrowCounterclockwise />}
                              onClick={() =>
                                handleActionClick(request, "sendback")
                              }
                            >
                              Send Back
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      {/* Action Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textTransform="capitalize">
            {actionType} Request
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box mb={4}>
              <Text fontWeight="semibold" mb={2}>
                Request: {selectedRequest?.title}
              </Text>
              <Text fontSize="sm" color="gray.600">
                Are you sure you want to {actionType} this request?
              </Text>
            </Box>
            <Textarea
              placeholder={`Add remarks (${actionType === "approve" ? "optional" : "required"})...`}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={4}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme={getActionButtonColor(actionType)}
              leftIcon={React.createElement(getActionButtonIcon(actionType))}
              onClick={handleActionSubmit}
              isLoading={actionLoading}
              isDisabled={actionType !== "approve" && !remarks}
            >
              {actionType === "approve"
                ? "Approve"
                : actionType === "reject"
                  ? "Reject"
                  : "Send Back"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ApprovalList;
